<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnDeleteResource',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'success']);

  const props = defineProps({
    ...userComponentProps,
    resource: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: null,
    },
    noIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    noText: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'sm',
    },
    tooltip: {
      type: String,
      required: false,
    },
    dialogHeader: {
      type: String,
      required: false,
    },
    dropdownItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    variant: {
      type: String,
      required: false,
      default: 'light',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    text: $fn.value.pgettext('BtnDeleteResource', 'Delete'),
    tooltip: $fn.value.pgettext('BtnDeleteResource|Tooltip', 'Delete'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDisabled = computed(() => {
    return props.disabled != null
      ? props.disabled
      : props.resource.meta &&
          props.resource.meta.deletable &&
          props.resource.meta.deletable.result === false
        ? true
        : false;
  });
  const currentTooltip = computed(() => {
    if (loaded.value) {
      return _.get(
        props.resource,
        'meta.deletable.message',
        props.tooltip || messages.value.tooltip
      );
    }
    return null;
  });

  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );
  watch(
    () => props.noIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noIcon');
    }
  );
  watch(
    () => props.noText,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noText');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.tooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('tooltip');
    }
  );
  watch(
    () => props.dialogHeader,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dialogHeader');
    }
  );
  watch(
    () => props.dropdownItem,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dropdownItem');
    }
  );
  watch(
    () => props.variant,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('variant');
    }
  );

  const act_7315092382398562_emit_yr481n18d_getActionArgs = async ($event) => {
    return {
      event: 'success',
    };
  };
  const act_7315092382398562_resource_pazjvj80n_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'delete',
      target: props.resource,
      data: { etag: props.resource.etag },
    };
  };
  const act_7315092382398562_resource_pazjvj80n_getConfirm = async ($event) => {
    return {
      header: props.dialogHeader,
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
      severity: 'warning',
    };
  };

  const act_7315092382398562_emit_yr481n18d = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_yr481n18d_getActionArgs,
  });
  const inst_evh_7315092382398562_success_0x4tci0wg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_yr481n18d,
      event: 'success',
      displayName: 'emit',
    });
  const act_7315092382398562_resource_pazjvj80n =
    new actionTypes.ResourceAction({
      actionArgs: act_7315092382398562_resource_pazjvj80n_getActionArgs,
      confirm: act_7315092382398562_resource_pazjvj80n_getConfirm,
      displayName: 'delete',
      events: [inst_evh_7315092382398562_success_0x4tci0wg],
    });
  const inst_evh_7315092382398562_clickPreventStop_5l0if5zkv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_resource_pazjvj80n,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_5l0if5zkv],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_success_0x4tci0wg,
    inst_evh_7315092382398562_clickPreventStop_5l0if5zkv,
    delete: inst_evh_7315092382398562_clickPreventStop_5l0if5zkv,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_success_0x4tci0wg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_success_0x4tci0wg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop_5l0if5zkv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_5l0if5zkv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :dropdownItem="dropdownItem"
    :text="noText ? null : messages.text"
    :icon="noIcon ? null : 'fa-trash'"
    :variant="dropdownItem ? 'default' : variant"
    :spinning="actions.delete.isRunning"
    :disabled="isDisabled"
    :size="size"
    :tooltip="currentTooltip"
    :style="{
      cursor: isDisabled ? 'not-allowed' : null,
    }"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
